// Pages/admin/FonctionnalitesPage.jsx
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiCode,
  FiHelpCircle,
  FiDatabase,
  FiServer,
  FiSearch,
  FiX
} from "react-icons/fi";
import {
  BsRocket,
  BsShieldCheck,
  BsListCheck,
  BsCalendarHeart,
  BsCashCoin,
  BsGear,
  BsPeople,
  BsGraphUpArrow
} from "react-icons/bs";
import {
  TbBrandGoogle,
  TbCloudLock,
  TbPackage,
  TbCalendarTime,
  TbUserStar,
  TbCoin
} from "react-icons/tb";
import { 
  FaUserLock,
  FaCogs,
  FaSyncAlt
} from "react-icons/fa";
import { 
  MdOutlineWorkspacePremium,
  MdOutlineSecurity
} from "react-icons/md";

export default function FonctionnalitesPage() {
  const [viewMode, setViewMode] = useState("dev");
  const [searchTerm, setSearchTerm] = useState("");

  // Données complètes avec icônes modernes
  const fonctionnalites = [
    {
      id: 1,
      category: "Authentification & Sécurité",
      icon: <BsShieldCheck className="text-secondary-500" size={24} />,
      color: "secondary",
      items: [
        {
          name: "Connexion utilisateur",
          route: "/login",
          description: "Système d'authentification sécurisé avec email/mot de passe",
          icon: <FaUserLock className="text-secondary-400" size={20} />,
          endpoints: ["POST /api/auth/login"],
          faq: {
            question: "Comment se connecter à l'application ?",
            answer: "Utilisez le formulaire de connexion avec votre email et mot de passe. Les comptes admin ont accès au panel d'administration.",
            usage: "Tous les utilisateurs doivent se connecter pour accéder à leurs fonctionnalités respectives."
          }
        },
        {
          name: "Inscription utilisateur",
          route: "/register",
          description: "Création de compte avec vérification par email sécurisée",
          icon: <BsShieldCheck className="text-green-400" size={20} />,
          endpoints: ["POST /api/user/register", "POST /api/user/verify"],
          faq: {
            question: "Comment créer un nouveau compte client ?",
            answer: "Les clients peuvent s'inscrire via le formulaire public. Un email de vérification est envoyé pour activer le compte.",
            usage: "Nouveaux clients - Processus entièrement automatisé"
          }
        },
        {
          name: "Authentification sociale",
          route: null,
          description: "Connexion instantanée via Google, Facebook, Apple",
          icon: <TbBrandGoogle className="text-purple-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Peut-on se connecter avec les réseaux sociaux ?",
            answer: "Cette fonctionnalité est prévue pour une version future. Actuellement, seule l'authentification par email est disponible.",
            usage: "Futur - Simplification de la connexion pour les utilisateurs"
          }
        },
        {
          name: "2FA - Double authentification",
          route: null,
          description: "Validation en deux étapes pour une sécurité maximale",
          icon: <TbCloudLock className="text-red-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Y a-t-il une authentification à deux facteurs ?",
            answer: "Non, cette fonctionnalité de sécurité avancée est planifiée pour une prochaine mise à jour.",
            usage: "Futur - Sécurité renforcée pour les comptes admin"
          }
        }
      ]
    },
    {
      id: 2,
      category: "Services & Catalogue",
      icon: <BsListCheck className="text-green-500" size={24} />,
      color: "green",
      items: [
        {
          name: "Catalogue des services",
          route: "/admin/catalogue",
          description: "Gestion intuitive des catégories et services",
          icon: <BsListCheck className="text-green-400" size={20} />,
          endpoints: ["GET /api/service/category", "POST /api/service/category", "PUT /api/service/category/{id}"],
          faq: {
            question: "Comment ajouter une nouvelle catégorie de service ?",
            answer: "Dans l'admin > Catalogue, cliquez sur 'Ajouter une catégorie'. Renseignez le nom et la description.",
            usage: "Administrateurs - Pour organiser les services par catégories"
          }
        },
        {
          name: "Services avec options avancées",
          route: null,
          description: "Services modulables avec variantes et personnalisation",
          icon: <TbPackage className="text-secondary-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Peut-on créer des services avec des options ?",
            answer: "Cette fonctionnalité est en cours de développement. Bientôt, vous pourrez ajouter des variantes à vos services.",
            usage: "En cours - Pour des services plus flexibles"
          }
        },
        {
          name: "Packages premium",
          route: null,
          description: "Création de forfaits exclusifs regroupant plusieurs services",
          icon: <MdOutlineWorkspacePremium className="text-purple-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Est-il possible de créer des forfaits ?",
            answer: "Pas encore, mais cette fonctionnalité permettra de créer des packages regroupant plusieurs services à prix avantageux.",
            usage: "Futur - Marketing et fidélisation clients"
          }
        }
      ]
    },
    {
      id: 3,
      category: "Réservations & Planning",
      icon: <BsCalendarHeart className="text-purple-500" size={24} />,
      color: "purple",
      items: [
        {
          name: "Création de réservation",
          route: "/services",
          description: "Prise de rendez-vous en ligne fluide et intuitive",
          icon: <TbCalendarTime className="text-purple-400" size={20} />,
          endpoints: ["POST /api/reservation/"],
          faq: {
            question: "Comment un client prend-il rendez-vous ?",
            answer: "Le client sélectionne les services désirés, choisit un créneau disponible et confirme la réservation.",
            usage: "Clients - Réservation en libre-service 24h/24"
          }
        },
        {
          name: "Rappels intelligents",
          route: null,
          description: "Notifications SMS/Email intelligentes avant le rendez-vous",
          icon: <BsCalendarHeart className="text-secondary-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Y a-t-il des rappels de rendez-vous ?",
            answer: "Cette fonctionnalité est en cours d'implémentation. Bientôt, les clients recevront des rappels automatiques.",
            usage: "En cours - Réduction des absences"
          }
        },
        {
          name: "Réservations récurrentes",
          route: null,
          description: "Abonnements et rendez-vous réguliers automatisés",
          icon: <FaSyncAlt className="text-green-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Peut-on programmer des rendez-vous récurrents ?",
            answer: "Pas encore disponible. Cette fonctionnalité permettra aux clients de programmer des rendez-vous réguliers.",
            usage: "Futur - Fidélisation et planning prévisible"
          }
        }
      ]
    },
    {
      id: 4,
      category: "Équipe & Utilisateurs",
      icon: <TbUserStar className="text-orange-500" size={24} />,
      color: "orange",
      items: [
        {
          name: "Gestion de l'équipe",
          route: "/admin/teams",
          description: "Gestion complète des membres de l'équipe",
          icon: <BsPeople className="text-orange-400" size={20} />,
          endpoints: ["POST /api/user/members", "GET /api/user/all/employee"],
          faq: {
            question: "Comment ajouter un nouveau membre à l'équipe ?",
            answer: "Dans l'admin > Équipe, utilisez le bouton 'Ajouter un membre'. Renseignez ses informations et son rôle.",
            usage: "Administrateurs - Gestion du personnel"
          }
        },
        {
          name: "Permissions avancées",
          route: null,
          description: "Système de permissions granulaires et flexibles",
          icon: <MdOutlineSecurity className="text-secondary-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Peut-on définir des permissions précises ?",
            answer: "En cours de développement. Bientôt, vous pourrez définir des permissions très précises pour chaque rôle.",
            usage: "En cours - Sécurité et contrôle d'accès"
          }
        }
      ]
    },
    {
      id: 5,
      category: "Ventes & Paiements",
      icon: <BsCashCoin className="text-red-500" size={24} />,
      color: "red",
      items: [
        {
          name: "Gestion des ventes",
          route: "/admin/sales",
          description: "Suivi en temps réel des transactions et performances",
          icon: <BsGraphUpArrow className="text-red-400" size={20} />,
          endpoints: ["GET /api/payments/", "POST /api/payments/"],
          faq: {
            question: "Où voir l'historique des ventes ?",
            answer: "Dans l'admin > Ventes, vous trouverez toutes les transactions avec les détails des paiements.",
            usage: "Administrateurs et Managers - Suivi financier"
          }
        },
        {
          name: "Paiements en ligne sécurisés",
          route: null,
          description: "Intégration de solutions de paiement en ligne modernes",
          icon: <TbCoin className="text-secondary-400" size={20} />,
          endpoints: [],
          faq: {
            question: "Peut-on payer en ligne ?",
            answer: "En cours d'implémentation. Bientôt, les clients pourront payer directement en ligne.",
            usage: "En cours - Convenance client et réduction du cash"
          }
        }
      ]
    },
    {
      id: 6,
      category: "Paramètres & Administration",
      icon: <BsGear className="text-gray-500" size={24} />,
      color: "gray",
      items: [
        {
          name: "Configuration du salon",
          route: "/admin/salon",
          description: "Paramétrage complet de l'établissement",
          icon: <FaCogs className="text-gray-400" size={20} />,
          endpoints: ["GET /api/salon/", "PUT /api/salon/{id}", "POST /api/salon/"],
          faq: {
            question: "Comment modifier les horaires d'ouverture ?",
            answer: "Dans l'admin > Paramètres > Salon, vous pouvez ajuster les horaires, la capacité et les jours de fermeture.",
            usage: "Administrateurs - Configuration de l'établissement"
          }
        }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      secondary: "bg-secondary-50 border-secondary-200 text-secondary-700",
      green: "bg-green-50 border-green-200 text-green-700", 
      purple: "bg-purple-50 border-purple-200 text-purple-700",
      orange: "bg-orange-50 border-orange-200 text-orange-700",
      red: "bg-red-50 border-red-200 text-red-700",
      gray: "bg-gray-50 border-gray-200 text-gray-700"
    };
    return colors[color] || colors.gray;
  };

  // Fonction de recherche
  const searchFunctionnalites = useMemo(() => {
    if (!searchTerm.trim()) {
      return fonctionnalites;
    }

    const searchLower = searchTerm.toLowerCase();
    
    return fonctionnalites.map(category => ({
      ...category,
      items: category.items.filter(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.faq.question.toLowerCase().includes(searchLower) ||
        item.faq.answer.toLowerCase().includes(searchLower) ||
        item.faq.usage.toLowerCase().includes(searchLower) ||
        category.category.toLowerCase().includes(searchLower) ||
        (item.endpoints && item.endpoints.some(endpoint => 
          endpoint.toLowerCase().includes(searchLower)
        ))
      )
    })).filter(category => category.items.length > 0);
  }, [searchTerm, fonctionnalites]);

  // Utiliser les données filtrées par recherche
  const displayedFonctionnalites = searchTerm ? searchFunctionnalites : fonctionnalites;

  // Vue Développeur
  const DevView = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {displayedFonctionnalites.map((category) => (
          <div 
            key={category.id}
            className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className={`border-b p-4 ${getColorClasses(category.color)}`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white  shadow-sm">
                  {category.icon}
                </div>
                <div>
                  <h2 className="font-poppins font-semibold text-[18px] text-title">{category.category}</h2>
                  <p className="font-nunito text-[14px] text-text">{category.items.length} fonctionnalités</p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {category.items.map((item, index) => (
                <div 
                  key={index}
                  className="border border-gray-100 hover:border-gray-300 transition-all duration-200 bg-white"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-50  border">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-poppins font-semibold text-[18px] text-title">{item.name}</h3>
                          <p className="font-nunito text-[14px] text-text mt-1">{item.description}</p>
                        </div>
                      </div>
                    </div>

                    {item.route && (
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 ">
                        <Link 
                          to={item.route}
                          className="font-nunito font-semibold text-[14px] text-action inline-flex items-center"
                        >
                          <BsRocket className="mr-2" size={14} />
                          Accéder : {item.route}
                        </Link>
                      </div>
                    )}

                    {item.endpoints && item.endpoints.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <FiServer className="text-gray-500" size={16} />
                          <div className="font-poppins font-medium text-[15px] text-subtitle">Endpoints API</div>
                        </div>
                        <div className="space-y-2">
                          {item.endpoints.map((endpoint, idx) => (
                            <div 
                              key={idx}
                              className="font-nunito text-[14px] bg-gray-900 text-gray-100 p-3  border border-gray-700 font-mono hover:bg-gray-800 transition-colors"
                            >
                              <div className="flex justify-between items-center">
                                <code>{endpoint}</code>
                                <FiDatabase className="text-gray-400" size={14} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!item.endpoints || item.endpoints.length === 0) && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 ">
                        <div className="font-nunito text-[14px] text-text italic flex items-center">
                          <BsRocket className="mr-2" size={14} />
                          Endpoints à définir lors du développement
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Vue FAQ Admin
  const FAQView = () => (
    <div className="space-y-6">
      <div className="space-y-6">
        {displayedFonctionnalites.map((category) => (
          <div key={category.id} className="bg-white border border-gray-200">
            <div className={`border-b p-6 ${getColorClasses(category.color)}`}>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white  shadow-sm">
                  {category.icon}
                </div>
                <h2 className="font-poppins font-semibold text-[18px] text-title">{category.category}</h2>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {category.items.map((item, index) => (
                <div key={index} className="border border-gray-200 p-6 hover:border-gray-300 transition-all duration-200 bg-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gray-50  border mt-1">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">{item.faq.question}</h3>
                        {item.route && (
                          <Link 
                            to={item.route}
                            className="font-nunito font-semibold text-[14px] text-action inline-flex items-center"
                          >
                            <BsRocket className="mr-2" />
                            Accéder à cette fonctionnalité
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 border border-gray-200 ">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-1 bg-secondary-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <h4 className="font-poppins font-medium text-[15px] text-subtitle mb-2">Réponse :</h4>
                        <p className="font-nunito text-[14px] text-text leading-relaxed">{item.faq.answer}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center space-x-4 font-nunito text-[14px]">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <FiHelpCircle size={14} />
                      <span><strong>Utilisation :</strong> {item.faq.usage}</span>
                    </div>
                  </div>

                  {item.endpoints && item.endpoints.length > 0 && (
                    <div className="mt-4 p-3 bg-secondary-50 border border-secondary-200 ">
                      <div className="flex items-center space-x-2 mb-2">
                        <FiCode size={14} className="text-secondary-600" />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">Points techniques :</span>
                      </div>
                      <div className="font-nunito text-[14px] text-secondary-700 font-mono">
                        {item.endpoints.join(' • ')}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête avec mode switch ET recherche */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">
                {viewMode === 'dev' ? '🚀 Documentation Développeur' : '📖 Guide Utilisateur'}
              </h1>
              <p className="font-nunito text-[14px] text-text">
                {viewMode === 'dev' 
                  ? 'Documentation technique complète des APIs et fonctionnalités' 
                  : 'Guide utilisateur et questions fréquentes pour les administrateurs'
                }
              </p>
            </div>

            <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4 mt-4 lg:mt-0">
              {/* Barre de recherche CENTRÉE */}
              <div className="relative lg:w-80 flex items-center">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" size={20} />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher une fonctionnalité..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-12 pr-10 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-center"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <FiX className="text-gray-400 hover:text-gray-600" size={18} />
                  </button>
                )}
              </div>

              {/* Switch Mode */}
              <div className="bg-white border border-gray-200 p-1 flex">
                <button
                  onClick={() => setViewMode('dev')}
                  className={`px-4 py-2 font-nunito font-semibold text-[14px] flex items-center space-x-2 ${
                    viewMode === 'dev' 
                      ? 'bg-secondary-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FiCode size={16} />
                  <span>Vue Développeur</span>
                </button>
                <button
                  onClick={() => setViewMode('faq')}
                  className={`px-4 py-2 font-nunito font-semibold text-[14px] flex items-center space-x-2 ${
                    viewMode === 'faq' 
                      ? 'bg-green-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FiHelpCircle size={16} />
                  <span>Vue Utilisateur</span>
                </button>
              </div>
            </div>
          </div>

          {/* Résultats de recherche */}
          {searchTerm && (
            <div className="bg-secondary-50 border border-secondary-200  p-4 mb-4">
              <div className="flex items-center space-x-2">
                <FiSearch className="text-secondary-500" size={18} />
                <span className="font-poppins font-medium text-[15px] text-subtitle">
                  {displayedFonctionnalites.flatMap(c => c.items).length} résultat(s) trouvé(s) pour "{searchTerm}"
                </span>
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-auto font-nunito font-semibold text-[14px] text-action hover:text-secondary-800"
                >
                  Effacer la recherche
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Message si aucun résultat */}
        {displayedFonctionnalites.length === 0 && (
          <div className="text-center py-12">
            <FiSearch className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">Aucun résultat trouvé</h3>
            <p className="font-nunito text-[14px] text-text mb-4">
              Aucune fonctionnalité ne correspond à votre recherche "{searchTerm}"
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 bg-secondary-500 text-white font-nunito font-semibold text-[14px] hover:bg-secondary-600 transition-colors"
            >
              Voir toutes les fonctionnalités
            </button>
          </div>
        )}

        {/* Contenu selon le mode */}
        {displayedFonctionnalites.length > 0 && (
          viewMode === 'dev' ? <DevView /> : <FAQView />
        )}
      </div>
    </div>
  );
}